import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { useSubmitArtifactAuditReviewMutation } from '@/services/api/artifactAuditService';
import { ArtifactAudit } from '@/services/types/blob-types';

interface AuditFlagOverlayProps {
  audit: ArtifactAudit;
  artifactId: string;
  matterId: string;
  onViewAnyway: () => void;
}

export const AuditFlagOverlay: React.FC<AuditFlagOverlayProps> = ({
  audit,
  artifactId,
  matterId,
  onViewAnyway,
}) => {
  const [showDeleteNotes, setShowDeleteNotes] = useState(false);
  const [deleteNotes, setDeleteNotes] = useState('');
  const [submitReview, { isLoading }] = useSubmitArtifactAuditReviewMutation();

  const mismatches = audit.result?.mismatches ?? [];
  const confidencePct = audit.confidence != null ? Math.round(audit.confidence * 100) : null;

  // Collect all evidence across all mismatches
  const allEvidence = mismatches.flatMap((m) => m.evidence);

  // Collect unique entity key:value pairs for the chips
  const entityPairs = new Map<string, string>();
  allEvidence.forEach((ev) => {
    if (ev.matter_fact_value) {
      entityPairs.set(ev.matter_fact_value, ev.matter_fact_key);
    }
  });

  const handleKeep = async () => {
    try {
      await submitReview({
        artifactId,
        matterId,
        blobId: audit.blob_id,
        body: { decision: 'override_safe', notes: 'Marked safe by reviewer' },
      }).unwrap();
      toast.success('Document marked as safe');
    } catch {
      toast.error('Failed to submit review');
    }
  };

  const handleDelete = async () => {
    if (!showDeleteNotes) {
      setShowDeleteNotes(true);
      return;
    }
    try {
      await submitReview({
        artifactId,
        matterId,
        blobId: audit.blob_id,
        body: { decision: 'confirm_mismatch', notes: deleteNotes || 'Confirmed mismatch by reviewer' },
      }).unwrap();
      toast.success('Document confirmed as mismatch');
    } catch {
      toast.error('Failed to submit review');
    }
  };

  return (
    <div className="h-full flex flex-col items-center justify-center p-10 gap-5">
      {/* Warning icon circle */}
      <div className="w-12 h-12 rounded-full bg-[#FEF3C7] flex items-center justify-center flex-shrink-0">
        <ExclamationTriangleIcon className="w-6 h-6 text-amber-700" />
      </div>

      {/* Heading + subtitle */}
      <div className="flex flex-col items-center text-center">
        <h3 className="text-sm font-semibold text-gray-800">
          Flagged for Review
        </h3>
        <p className="text-xs font-medium text-gray-500 mt-0.5">
          This document may not be related to this matter.
        </p>
      </div>

      {/* Details card */}
      <div className="w-[380px] max-h-[400px] overflow-y-auto rounded-lg border border-[#FDE68A] bg-[#FFFBEB] p-4 space-y-3">
        {/* Relevance score */}
        {confidencePct != null && (
          <div className="space-y-0.5">
            <p className="text-[10px] font-semibold text-[#D97706] uppercase tracking-wide">
              Relevance Score
            </p>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-1.5 bg-[#FDE68A] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#D97706] rounded-full"
                  style={{ width: `${confidencePct}%` }}
                />
              </div>
              <span className="text-xs font-semibold text-[#D97706]">
                {confidencePct}%
              </span>
            </div>
          </div>
        )}

        {/* Evidence details — quotes from the document */}
        {allEvidence.length > 0 && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-semibold text-[#D97706] uppercase tracking-wide">
              Evidence
            </p>
            {allEvidence.map((ev, i) => (
              <div key={i} className="space-y-1">
                <p className="text-[11px] font-medium text-gray-700">
                  {ev.summary}
                </p>
                {ev.artifact_quote && (
                  <blockquote className="text-[11px] text-gray-500 italic border-l-2 border-[#FDE68A] pl-2 leading-relaxed">
                    &ldquo;{ev.artifact_quote}&rdquo;
                  </blockquote>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Detected entities */}
        {entityPairs.size > 0 && (
          <div className="space-y-1">
            <p className="text-[10px] font-semibold text-[#D97706] uppercase tracking-wide">
              Detected Entities
            </p>
            <div className="flex flex-wrap gap-1.5">
              {[...entityPairs.entries()].map(([value, _key]) => (
                <span
                  key={value}
                  className="px-2 py-0.5 text-[11px] font-medium text-[#92400E] bg-[#FEF3C7] border border-[#FDE68A] rounded"
                >
                  {value}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Delete notes */}
      {showDeleteNotes && (
        <textarea
          className="w-[340px] text-sm border border-amber-200 rounded-lg p-3 resize-none bg-white"
          rows={2}
          placeholder="Optional notes about this mismatch..."
          value={deleteNotes}
          onChange={(e) => setDeleteNotes(e.target.value)}
        />
      )}

      {/* Action buttons — Keep = dark filled, Delete = red outline (matching design) */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="px-5 py-2 rounded-md bg-gray-800 text-white text-sm font-medium hover:bg-gray-900 transition-colors disabled:opacity-50"
          onClick={handleKeep}
          disabled={isLoading}
        >
          Keep Document
        </button>
        <button
          type="button"
          className="px-5 py-2 rounded-md bg-red-50 border border-red-300 text-red-600 text-sm font-medium hover:bg-red-100 transition-colors disabled:opacity-50"
          onClick={handleDelete}
          disabled={isLoading}
        >
          {showDeleteNotes ? 'Confirm Delete' : 'Delete Document'}
        </button>
      </div>

      {/* View anyway link */}
      <button
        type="button"
        className="text-xs text-gray-500 hover:text-gray-700 underline underline-offset-2 font-medium"
        onClick={onViewAnyway}
      >
        View document anyway
      </button>
    </div>
  );
};
